import { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { QuoteCard } from '../components/QuoteCard'
import { OptionsSection } from '../components/OptionsSection'
import { TickerPendingOrders } from '../components/TickerPendingOrders'
import { Level2Panel } from '../components/Level2Panel'
import { TickerPriceChart, GRID_DIMS, type ChartGridLayout } from '../components/TickerPriceChart'
import type { BarInterval } from '../api/marketData'
import { ChartErrorBoundary } from '../components/chart/ChartErrorBoundary'
import { GridDividers } from '../components/chart/GridDividers'
import { PositionCard } from '../components/active/ActiveTradeCard'
import { InstrumentHeader, type InstrumentView } from '../components/instrument/InstrumentHeader'
import { OverviewTab } from '../components/instrument/OverviewTab'
import { useAuthStore } from '../auth/useAuthStore'
import { useSetting } from '../hooks/useSetting'
import { useGridResize } from '../hooks/useGridResize'
import { usePanelFrame } from '../hooks/usePanelFrame'
import { useActiveTrades } from '../hooks/useActiveTrades'
import { underlyingOf } from '../lib/activeTrades'

export type Tab = 'stock' | 'options'

const DEFAULT_TAB_KEY = 'default-ticker-tab'
const PANEL_MIN_KEY = 'ticker-trade-panel-minimized'
const CHART_LAYOUT_KEY = 'ticker-chart-layout'
const CHART_GRID_INTERVALS_KEY = 'ticker-chart-grid-intervals'
const CHART_GRID_SIZES_KEY = 'ticker-chart-grid-sizes'
const DEFAULT_GRID_INTERVALS: BarInterval[] = ['1m', '5m', '15m', '1h']

export function InstrumentDetails() {
  const { symbol = '' } = useParams<{ symbol: string }>()
  const upper = symbol.toUpperCase()
  const [searchParams, setSearchParams] = useSearchParams()
  const optionsSupported = useAuthStore((s) => s.capabilities?.options ?? false)
  const schwabAttached = useAuthStore((s) =>
    s.platforms.some((p) => p.platformId === 'schwab'),
  )

  // Top-level view. Charts is the default (preserves the trading flow); Overview
  // is opt-in via ?view=overview.
  const view: InstrumentView = searchParams.get('view') === 'overview' ? 'overview' : 'charts'
  function selectView(next: InstrumentView) {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev)
        if (next === 'overview') p.set('view', 'overview')
        else p.delete('view')
        return p
      },
      { replace: true },
    )
  }

  const defaultTab = useSetting<Tab>(DEFAULT_TAB_KEY, 'stock')
  const requestedTab: Tab = (() => {
    const param = searchParams.get('tab')
    if (param === 'options' || param === 'stock') return param
    return defaultTab[0]
  })()
  const tab: Tab = (() => {
    if (requestedTab === 'options' && !optionsSupported) return 'stock'
    return requestedTab
  })()

  useEffect(() => {
    if (requestedTab === 'options' && !optionsSupported) {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev)
          p.delete('tab')
          return p
        },
        { replace: true },
      )
    }
  }, [requestedTab, optionsSupported, setSearchParams])

  function selectTab(next: Tab) {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev)
        p.set('tab', next)
        return p
      },
      { replace: true },
    )
  }

  const { positions, quoteThrottled } = useActiveTrades()
  const tickerPositions = useMemo(
    () =>
      positions.filter((p) => {
        const sym = p.position.instrument.symbol
        if (p.position.instrument.type === 'OPTION') return underlyingOf(sym) === upper
        return sym === upper
      }),
    [positions, upper],
  )
  const hasPosition = tickerPositions.length > 0

  const [minimized, setMinimized] = useSetting<boolean>(PANEL_MIN_KEY, false)
  const [chartLayout, setChartLayout] = useSetting<ChartGridLayout>(CHART_LAYOUT_KEY, 'single')
  const [gridIntervals, setGridIntervals] = useSetting<BarInterval[]>(
    CHART_GRID_INTERVALS_KEY,
    DEFAULT_GRID_INTERVALS,
  )
  const { cols: gridCols, rows: gridRows, count: chartCount } = GRID_DIMS[chartLayout] ?? GRID_DIMS.single
  const grid = useGridResize(CHART_GRID_SIZES_KEY, chartLayout, gridCols, gridRows)
  // Which grid cell is active. The active cell owns keyboard control (digit
  // interval modal, Alt+R) and shows a highlight outline. Clamped so it stays
  // valid when the layout shrinks.
  const [activeChart, setActiveChart] = useState(0)
  const activeCell = Math.min(activeChart, chartCount - 1)
  const tradeFrame = usePanelFrame('ticker-trade-frame', { minHeight: 320 })
  const panelLabel = hasPosition ? `Position · ${upper}` : `Trade · ${upper}`
  // The compact one-row trade bar only makes sense for the quick shares ticket.
  // Positions and the options tab keep a label bar when collapsed.
  const showCompactBar = !hasPosition && tab === 'stock'

  return (
    <div className="flex flex-col h-full min-h-0 lg:h-auto">
      <InstrumentHeader
        view={view}
        onSelectView={selectView}
        layout={chartLayout}
        onLayoutChange={
          schwabAttached
            ? (l) => {
                grid.resetForLayoutChange()
                setChartLayout(l)
              }
            : undefined
        }
      />

      {view === 'overview' ? (
        <OverviewTab symbol={upper} />
      ) : (
        // Mobile: flex column filling the area below the header so the chart owns
        // a flex-1 slot and shrinks as the docked panel grows. Desktop: normal
        // flow with the panel floating (lg:fixed).
        <div className="flex flex-col flex-1 min-h-0 lg:block lg:h-auto lg:flex-none divide-y divide-zinc-800">
          <div className="flex-1 min-h-0 lg:flex-none flex flex-col">
            {schwabAttached ? (
              chartLayout === 'single' ? (
                <ChartErrorBoundary resetKey={upper}>
                  <TickerPriceChart symbol={upper} fill panelOpen={!minimized} />
                </ChartErrorBoundary>
              ) : (
                <div
                  ref={grid.gridRef}
                  className="relative grid gap-px bg-zinc-800 border-b border-zinc-800 flex-1 min-h-0 lg:flex-none lg:h-[calc(100dvh-12rem)]"
                  style={{
                    gridTemplateColumns: grid.gridTemplateColumns,
                    gridTemplateRows: grid.gridTemplateRows,
                  }}
                >
                  {Array.from({ length: chartCount }, (_, i) => (
                    <div
                      key={i}
                      onPointerDownCapture={() => setActiveChart(i)}
                      className={`relative min-h-0 min-w-0 flex flex-col bg-zinc-950 ${
                        i === activeCell ? 'ring-2 ring-sky-500 ring-inset z-10' : ''
                      }`}
                    >
                      <ChartErrorBoundary resetKey={`${upper}:${i}`}>
                        <TickerPriceChart
                          symbol={upper}
                          gridCell
                          active={i === activeCell}
                          panelOpen={!minimized}
                          defaultInterval={gridIntervals[i] ?? DEFAULT_GRID_INTERVALS[i]}
                          onIntervalChange={(iv) => {
                            const next = [...DEFAULT_GRID_INTERVALS].map(
                              (d, idx) => gridIntervals[idx] ?? d,
                            )
                            next[i] = iv
                            setGridIntervals(next)
                          }}
                        />
                      </ChartErrorBoundary>
                    </div>
                  ))}
                  <GridDividers grid={grid} />
                </div>
              )
            ) : (
              <div className="px-4 py-6 text-sm text-zinc-500">
                Connect a Schwab account to see price charts.
              </div>
            )}
          </div>
          {schwabAttached && <Level2Panel symbol={upper} />}
          <div
            ref={tradeFrame.ref}
            style={tradeFrame.style(!minimized)}
            onClick={(e) => {
              // Collapse only via the header chevron — clicking the panel body
              // (a non-interactive gap) must not dismiss an open ticket. The
              // container click only handles expanding the collapsed bar.
              if (!minimized) return
              if (!window.matchMedia('(min-width: 1024px)').matches) return
              if (tradeFrame.consumeDragClick()) return
              const target = e.target as HTMLElement
              if (target.closest('button, input, select, textarea, a, label, [role="button"], [role="tab"]'))
                return
              setMinimized(false)
            }}
            className={`shrink-0 z-30 border-zinc-800 bg-zinc-950/95 shadow-2xl backdrop-blur-md max-lg:border-t max-lg:border-x-0 max-lg:border-b-0 lg:fixed lg:right-20 lg:bottom-0 lg:rounded-t-lg lg:border lg:cursor-pointer lg:w-[540px] ${
              tradeFrame.detached ? 'lg:rounded-b-lg' : 'lg:border-b-0'
            } ${
              minimized
                ? 'overflow-hidden lg:max-h-[36px]'
                : 'flex flex-col overflow-y-auto scrollbar-hide max-lg:max-h-[45dvh] lg:min-h-[420px] lg:max-h-[calc(100dvh-4rem)]'
            }`}
          >
            {minimized ? (
              <>
                {/* Mobile collapsed: functional compact trade bar (shares), else a label bar. */}
                <div className="lg:hidden">
                  {showCompactBar ? (
                    <QuoteCard symbol={upper} collapsed onExpand={() => setMinimized(false)} />
                  ) : (
                    <PanelHeaderBar
                      label={panelLabel}
                      minimized
                      onToggle={() => setMinimized(false)}
                    />
                  )}
                </div>
                {/* Desktop collapsed: label bar (unchanged). */}
                <div className="hidden lg:block">
                  <PanelHeaderBar
                    label={panelLabel}
                    minimized
                    onToggle={() => setMinimized(true)}
                    onExpand={() => setMinimized(false)}
                    onPointerDown={tradeFrame.onHeaderPointerDown}
                    detached={tradeFrame.detached}
                    onDock={tradeFrame.dock}
                  />
                </div>
              </>
            ) : (
              <>
                <PanelHeaderBar
                  label={panelLabel}
                  minimized={false}
                  onToggle={() => setMinimized(true)}
                  onPointerDown={tradeFrame.onHeaderPointerDown}
                  detached={tradeFrame.detached}
                  onDock={tradeFrame.dock}
                />
                <TickerPendingOrders symbol={upper} />
                {hasPosition ? (
                  <div className="flex flex-col gap-3 p-3 pb-10 lg:pb-3">
                    {tickerPositions.map((row) => (
                      <PositionCard
                        key={`${row.position.accountId ?? ''}:${row.position.instrument.symbol}:${row.position.openedAt}`}
                        row={row}
                        liveThrottled={quoteThrottled}
                      />
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="flex border-b border-zinc-800 px-2 sm:px-4 bg-white/[0.02]">
                      <TabButton active={tab === 'stock'} onClick={() => selectTab('stock')}>
                        Shares
                      </TabButton>
                      {optionsSupported && (
                        <TabButton active={tab === 'options'} onClick={() => selectTab('options')}>
                          Options
                        </TabButton>
                      )}
                    </div>
                    <div
                      className={`pb-10 lg:pb-3 ${
                        tradeFrame.detached ? '' : 'flex-1 min-h-0 flex flex-col'
                      }`}
                    >
                      {tab === 'stock' ? (
                        <QuoteCard symbol={upper} dense />
                      ) : (
                        <OptionsSection symbol={upper} dense />
                      )}
                    </div>
                  </>
                )}
                {tradeFrame.detached && (
                  <div
                    onPointerDown={tradeFrame.onResizePointerDown}
                    title="Drag to resize"
                    className="hidden lg:block sticky bottom-0 h-2 cursor-ns-resize hover:bg-white/10 transition"
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

interface PanelHeaderBarProps {
  label: string
  minimized: boolean
  onToggle: () => void
  /** Optional separate expand handler (desktop label bar where onToggle minimizes). */
  onExpand?: () => void
  /** Desktop drag-to-detach handle (usePanelFrame). */
  onPointerDown?: (e: React.PointerEvent) => void
  detached?: boolean
  onDock?: () => void
}

function PanelHeaderBar({
  label,
  minimized,
  onToggle,
  onExpand,
  onPointerDown,
  detached,
  onDock,
}: PanelHeaderBarProps) {
  const handle = minimized && onExpand ? onExpand : onToggle
  return (
    <div
      onPointerDown={onPointerDown}
      className="shrink-0 flex items-center justify-between gap-2 px-3 h-[36px] border-b border-zinc-800 bg-white/[0.02] lg:rounded-t-lg sticky top-0 bg-zinc-950/95 backdrop-blur-md z-20 select-none"
    >
      <span className="flex-1 min-w-0 text-xs uppercase tracking-wide text-zinc-400 font-medium truncate">
        {label}
      </span>
      {detached && onDock && (
        <button
          type="button"
          onClick={onDock}
          aria-label="Dock panel to bottom"
          title="Dock to bottom"
          className="p-1 rounded text-zinc-400 hover:text-zinc-100 hover:bg-white/5 transition"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M12 3v11" />
            <polyline points="7 9 12 14 17 9" />
            <line x1="5" y1="20" x2="19" y2="20" />
          </svg>
        </button>
      )}
      <button
        type="button"
        onClick={handle}
        aria-label={minimized ? 'Expand trade panel' : 'Minimize trade panel'}
        title={minimized ? 'Expand' : 'Minimize'}
        className="p-1 rounded text-zinc-400 hover:text-zinc-100 hover:bg-white/5 transition"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`transition-transform ${minimized ? 'rotate-180' : ''}`}
          aria-hidden
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
    </div>
  )
}

interface TabButtonProps {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}

function TabButton({ active, onClick, children }: TabButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 sm:px-4 py-2 text-sm font-medium -mb-px border-b-2 transition ${
        active
          ? 'border-emerald-500 text-zinc-100'
          : 'border-transparent text-zinc-500 hover:text-zinc-300'
      }`}
    >
      {children}
    </button>
  )
}
